import { useEffect, useState } from 'react';
import { faArrowLeft, faTimes } from '@fortawesome/pro-light-svg-icons';
import { AnimatePresence, motion } from 'framer-motion';

import useTimer from '@/hooks/use-timer.hook';
import { Button } from '@/components/ui/button/button.component';
import { SheetClose, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Icon } from '@/components/ui/icon.component';
import {
  RegistrationData,
  setRegistrationStorage,
  getRegistrationStorage,
  removeRegistrationStorage,
} from '@/storage/registration.storage';
import { setAuthTokens } from '@/storage/auth-tokens.storage';
import { resendEmailValidationCode, sendEmailValidationCode } from '@/services/auth/registration.service';
import { StepKeys } from '@/app/(main)/_components/sign-in-modal/_types/header-auth-modal-step-keys.enum';
import { useToast } from '@/components/ui/use-toast';
import { resolveAuthenticatedUser } from '@/store/auth.store';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import PinInput from '@/app/(main)/_components/sign-in-modal/_components/pin.component';
import { getLogger } from '@/logger/logger';

export default function PinConfirmationStep() {
  const { toast } = useToast();
  const [pin, setPin] = useState('');
  const [wrongPinError, setWrongPinError] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [token, setToken] = useState('');
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);
  // const [lastPinRequest, setLastPinRequest] = useState<Date>();
  const { timeLeft, startTimer, resetTimer, isActive, setTimeLeft } = useTimer({ seconds: 60 });

  const { handleJump: jump } = useProgressiveModal<StepKeys>();

  useEffect(() => {
    const registrationData: RegistrationData | null = getRegistrationStorage();
    setMaskedEmail(registrationData?.maskedEmail || '');
    setToken(registrationData?.token || '');
    setDocumentNumber(registrationData?.formData.documentNumber || '');
    if (registrationData?.lastPinRequest) {
      const diff = new Date().getTime() - new Date(registrationData.lastPinRequest).getTime();
      const diffSeconds = Math.floor(diff / 1000);
      const diffTime = 60 - diffSeconds;

      if (diffTime > 0) {
        setTimeLeft(diffTime);
        startTimer();
      }
    }
  }, [jump, setTimeLeft, startTimer]);

  useEffect(() => {
    async function validatePin() {
      try {
        const response = await sendEmailValidationCode(pin, token);
        const { access_token, refresh_token } = response;
        setAuthTokens({ access_token, refresh_token });
        removeRegistrationStorage();
        await resolveAuthenticatedUser(access_token);
        toast.success('Conta criada com sucesso!');
        jump({ key: StepKeys.CLOSE });
      } catch (error) {
        const logger = getLogger('SignInModal - PinConfirmationStep');
        logger.error('Error validating pin', { pin, error });
        setWrongPinError('Código inválido');
      } finally {
        setIsValidatingEmail(false);
      }
    }

    if (isValidatingEmail) {
      validatePin();
    }
  }, [isValidatingEmail, jump, pin, token, toast]);

  useEffect(() => {
    if (pin.length === 4) {
      setIsValidatingEmail(true);
    } else {
      setWrongPinError('');
    }
  }, [pin]);

  const handleResend = () => {
    resetTimer();
    startTimer();
    const registrationData = getRegistrationStorage();
    if (registrationData) {
      setRegistrationStorage({ ...registrationData, lastPinRequest: new Date().toISOString() });
      resendEmailValidationCode(documentNumber, token);
    }
  };

  const handleBack = () => {
    jump({ key: StepKeys.REGISTER, returning: true });
  };

  return (
    <div
      className="h-full flex flex-col outline-none outline-offset-0"
      onFocusCapture={(event) => event.preventDefault()}
      onFocus={(event) => event.preventDefault()}
      onKeyDown={(e) => e.key === 'Tab' && e.preventDefault()}
      tabIndex={-1}
    >
      <SheetHeader className="bg-white">
        <div className="flex justify-between gap-[10px] pb-6 items-center">
          <div className="flex gap-[10px]">
            <div className="flex justify-center w-[30px] h-[30px]">
              <Icon
                icon={faArrowLeft}
                className="cursor-pointer text-gray-500 text-2xl self-center"
                onClick={handleBack}
                data-testid="confirmation-icon--back"
              />
            </div>
            <SheetTitle>
              <div className="flex gap-[10px]">
                <span
                  className="font-extrabold text-2xl text-gray-900 text-nowrap"
                  data-testid="confirmation-span--confirmation-step"
                >
                  Validação de conta
                </span>
              </div>
            </SheetTitle>
          </div>
          <SheetClose asChild className="text-gray-500 cursor-pointer self-center px-2 hover:bg-gray-50">
            <Icon icon={faTimes} size="xl" className="hover:text-gray-900 text-[1.625rem]" />
          </SheetClose>
        </div>
      </SheetHeader>

      <div className="pb-6 flex flex-col">
        <div className="pb-6">
          <span className="text-gray-800 text-base">
            Insira o código de 4 dígitos que enviamos para o seu e-mail cadastrado:
            <br></br>
            <span className="font-semibold">{maskedEmail}</span>
          </span>
        </div>
        <div className="flex justify-center pb-11 flex-col">
          <PinInput onPinChange={setPin} withCodePasteButton={!wrongPinError.length} />
          {wrongPinError && wrongPinError.length && <span className="text-red-500 text-sm pt-2">{wrongPinError}</span>}
        </div>

        <div className="pb-4">
          <p className="text-center text-gray-800 font-light">
            Ainda não recebeu o código?
            {' '}
            <br></br>
            Clique para reenviar.
          </p>
        </div>

        <Button variant="secondary" color="primary" className="w-full" disabled={isActive} onClick={handleResend}>
          Reenviar
        </Button>
        <AnimatePresence initial={true}>
          {isActive && (
            <motion.div
              key="timer"
              initial={{ opacity: 0, height: 0, y: -80 }}
              animate={{ opacity: 1, height: 'auto', y: 0 }}
              exit={{ opacity: 0, height: 0 }}
              transition={{
                x: { type: 'keyframes', duration: 0.2 },
              }}
            >
              <div className="py-4">
                <p className="text-center text-gray-800 font-light">
                  Você pode reenviar o código em
                  {' '}
                  {timeLeft}
                  {' '}
                  segundos.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
